/**
 * The put to storage: the middle step of the intake door (PRD-80). The
 * api minted a PUT url for one object in the org's data bucket; the
 * bytes go straight there with the content type the signature covers,
 * never through the api (Cloud Run caps a request body at 32 MB; a real
 * IFC is over 100). An
 * XMLHttpRequest, because fetch has no upload progress and a 115 MB
 * put with no needle is a frozen button.
 */

/** What a caller holds to stop a put that is still running. */
export type PutHandle = { abort: () => void };

/**
 * Put the file to the minted url, reporting bytes sent as it goes.
 *
 * EVERY ENDING SETTLES THE PROMISE (2026-09-11): a timeout or an abort used to settle nothing, so
 * a caller that stopped listening left the transfer running with a promise nobody could resolve.
 * `handle` is filled with the means to stop it, for a caller that unmounts mid-put.
 */
export function putToUrl(
  putUrl: string,
  contentType: string,
  file: File,
  onProgress: (sentBytes: number, totalBytes: number) => void,
  handle?: { current: PutHandle | null },
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", putUrl);
    request.setRequestHeader("Content-Type", contentType);
    if (handle) handle.current = { abort: () => request.abort() };
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total);
    };
    request.onload = () => {
      // GCS answers a finished PUT with 200 or 201; anything else is the verdict.
      if (request.status === 200 || request.status === 201) resolve();
      else reject(new Error(`storage refused the upload: ${request.status}`));
    };
    request.onerror = () =>
      reject(new Error("storage unreachable during the upload"));
    request.ontimeout = () => reject(new Error("the upload timed out"));
    request.onabort = () => reject(new Error("the upload was stopped"));
    request.onloadend = () => {
      if (handle) handle.current = null;
    };
    request.send(file);
  });
}
